import { deadlineIn } from './date'
import { Store } from '~/types/store'

type ActiveSentStatus = 'SENT' | 'UNDER REVIEW' | 'REVIEWED' | 'CHOOSING A WINNER' | 'WON'
type CloseSentStatus = 'DECLINED' | 'DRAW CLOSED' | 'AWARDED' | 'MISSED' | 'NOT SELECTED'

export const SCHOLARSHIP_STATUS = {
  UNKNOWN: 0,
  INCOMPLETE: 1,
  IN_PROGRESS: 2,
  READY_TO_SUBMIT: 3,
  SUBMITTED: 4
}

export const STATUS_ID_CANCELED = 5

export const FILTER_SEARCH = 'search'
export const FILTER_APPLICANT_COUNT = 'applicant_count'
export const FILTER_DEADLINE = 'deadline'
export const FILTER_AMOUNT = 'amount'
export const FILTER_REQ_TYPE = 'req_type'
export const FILTER_CONF_SCORE = 'conf_score'
export const FILTER_CATEGORIES = 'categories'
export const FILTER_PAYMENT = 'payment'

export const filterList = [
  FILTER_SEARCH,
  FILTER_APPLICANT_COUNT,
  FILTER_DEADLINE,
  FILTER_AMOUNT,
  FILTER_REQ_TYPE,
  FILTER_CONF_SCORE,
  FILTER_CATEGORIES,
  FILTER_PAYMENT
]

export const filterBy = (list: Scholarship.General[], cbCondition: (item: Scholarship.General) => boolean): Scholarship.General[] => list.filter(item => cbCondition(item))

export const suitableCardsOnTop = (list: Scholarship.General[], propName: (item: Scholarship.General) => boolean): Scholarship.General[] => {
  const suitable: Scholarship.General[] = []

  return suitable.concat(list.filter((item) => {
    if (propName(item)) {
      suitable.push(item)
      return false
    }

    return true
  }))
}

const activeSentScholarships: ActiveSentStatus[] = ['SENT', 'UNDER REVIEW', 'REVIEWED', 'CHOOSING A WINNER', 'WON']
const closedSentScholarships: CloseSentStatus[] = ['DECLINED', 'DRAW CLOSED', 'AWARDED', 'MISSED', 'NOT SELECTED']

export const filterActiveStatuses = (list: Scholarship.GeneralSent[]) => {
  return list.filter(item => item.derivedStatus && activeSentScholarships.includes(item.derivedStatus as ActiveSentStatus))
}

export const filterClosedStatus = (list: Scholarship.GeneralSent[]) => {
  return list.filter(item => (item.derivedStatus && closedSentScholarships.includes(item.derivedStatus as CloseSentStatus)))
}

export const filterMethods = {
  [FILTER_SEARCH]: {
    apply: (filterData: Store.Filter.Search, list: Scholarship.General[]) => {
      if (!list) { return }

      let searchTerm = filterData.searchTerm
      if (searchTerm.length >= 3) {
        searchTerm = searchTerm.toLowerCase().trim()
        list = list.filter((item: Scholarship.General) => {
          const haystack = (item.title + item.description).toLowerCase()
          return haystack.includes(searchTerm)
        })
      }

      return list
    }
  },
  [FILTER_APPLICANT_COUNT]: {
    apply: (filterData: Store.Filter.Range, list: Scholarship.General[]) => {
      if (!list) { return }

      const max = filterData.max
      const min = filterData.min

      list = list.filter((item: Scholarship.General) => {
        return item.applicationCount >= min && item.applicationCount <= max
      })

      return list
    }
  },
  [FILTER_DEADLINE]: {
    apply: (filterData: Store.Filter.Val, list: Scholarship.General[]) => {
      if (!list) { return }
      if (!filterData.selected) { return }

      const selected = Number(filterData.selected)
      const compare = [
        (diffDays: number): boolean => diffDays < 1,
        (diffDays: number): boolean => diffDays >= 1 && diffDays < 7,
        (diffDays: number): boolean => diffDays >= 7 && diffDays < 28,
        (diffDays: number): boolean => diffDays >= 28 && diffDays > 28
      ]

      list = list.filter((item) => {
        const diffDays = deadlineIn(item.expirationDate)
        if (!diffDays) { return false }
        return compare[selected](diffDays)
      })

      return list
    }
  },
  [FILTER_AMOUNT]: {
    apply: (filterData: Store.Filter.Range, list: Scholarship.General[]) => {
      if (!list) { return }

      const max = filterData.max
      const min = filterData.min

      list = list.filter((item) => {
        return item.amount >= min && item.amount <= max
      })

      return list
    }
  },
  [FILTER_REQ_TYPE]: {
    apply: (filterData: Store.Filter.Set & { required?: string[] }, list: Scholarship.General[]) => {
      if (!list) { return }

      const selectedReqNames = [
        'Essay',
        'Link',
        'Document',
        'Survey',
        'Photo',
        'Question',
        'Video',
        'Special eligibility',
        'Goal',
        'No requirements'
      ].filter((...args) => {
        return filterData.selected.includes(args[1])
      })

      list = list.filter((item) => {
        if (selectedReqNames.includes('No requirements') && item.requirementList === '') {
          return false
        }
        const intersection = selectedReqNames.filter(v => v !== 'No requirements' && item.requirementList.includes(v))

        return intersection.length === 0
      })

      if (filterData.required && filterData.required.length > 0) {
        list = list.filter((item) => {
          return filterData.required!.some(v => item.requirementList.includes(v))
        })
      }

      return list
    }
  },
  [FILTER_CONF_SCORE]: {
    apply: (filterData: Store.Filter.SetStringValues, list: Scholarship.General[]) => {
      const selected = filterData.selected
      const compare = [
        (score: number) => score >= 1 && score <= 35,
        (score: number) => score >= 36 && score <= 69,
        (score: number) => score >= 70 && score < 99,
        (score: number) => score === 100
      ]

      list = list.filter((item) => {
        const score = +item.confidenceScore
        let isInRange = false

        selected.some((v) => {
          isInRange = compare[v](score)
          return isInRange
        })

        return isInRange
      })

      return list
    }
  },
  [FILTER_CATEGORIES]: {
    apply: (filterData: Store.Filter.SetStringValues, list: Scholarship.General[]) => {
      if (!list) { return }

      const appliedCategoryIDs = filterData.selected

      return list.filter((scholarship) => {
        return scholarship.categories.some(cat => appliedCategoryIDs.includes(cat.id))
      })
    }
  },
  [FILTER_PAYMENT]: {
    apply: (filterData: Store.Filter.SetStringValues, list: Scholarship.General[]) => {
      if (!list) { return }

      const appliedPaymentIDs = filterData.selected

      return list.filter((scholarship) => {
        if (!scholarship.payoutMethod?.length) {
          return appliedPaymentIDs.includes('4')
        }
        return appliedPaymentIDs.includes(String(scholarship.payoutMethod[0].id))
      })
    }
  }
}

export function applyMailboxFilters(resource, filters) {
  const parameters = Object.keys(filters)
  let result = resource
  parameters.forEach((filterName) => {
    const filterFuncName = 'filterBy' +
      filterName[0].toUpperCase() +
      filterName.substring(1)
    result = mailFilterMethods[filterFuncName](result, filters[filterName])
  })

  return result
}

const filterByQuery = (list, query) => {
  const filtered = []
  const props = ['sender', 'recipient', 'subject', 'clearBody']
  query = query.trim()

  if (!query) {
    return list
  }
  if (list === null) { return }

  for (let i = 0; i < list.length; i += 1) {
    const mail = list[i]

    for (let j = 0; j < props.length; j += 1) {
      if (mail[props[j]].includes(query) ||
        mail[props[j]].toUpperCase().includes(query) ||
        mail[props[j]].toLowerCase().includes(query)) {
        filtered.push(mail)
        break
      }
    }
  }

  return filtered
}
const mailFilterMethods = {
  filterByQuery
}
